import {
  AggregateFunction,
  WindowFunction,
} from 'src/modals/ColumnSettings/TableCustomColumnModal/TableCustomColumnModal.types';
import * as ts from 'typescript';

import { reportValueColumnNameToExpression } from '../reportsUtils';

/**
 * Custom column name for reporting v2 should follow the format of `__custom_{columnId}_displayName`
 * for example: `__custom_1234_Display Name`
 *
 * Keep this the same with ReportingV2TableRecord.cs CustomColumnNamePrefix
 */
export const CUSTOM_COLUMN_NAME_PREFIX = '__custom';

export const isCustomColumnName = (columnName: string): boolean => {
  return columnName.startsWith(CUSTOM_COLUMN_NAME_PREFIX);
};

export const getCustomColumnId = (columnName: string): string => {
  return columnName.substring(CUSTOM_COLUMN_NAME_PREFIX.length).split('_')[1];
};

export const getCustomColumnDisplayName = (columnName: string): string => {
  return columnName
    .substring(CUSTOM_COLUMN_NAME_PREFIX.length)
    .split('_')
    .slice(2)
    .join('_');
};

export const getColumnNameFromCustomColumnId = (
  customColumnId: string,
  customColumnDisplayName: string
): string => {
  return `${CUSTOM_COLUMN_NAME_PREFIX}_${customColumnId}_${customColumnDisplayName}`;
};

/**
 * Converts token (representation on the UI) to expression (representation in the backend)
 *
 * Example In 1: ['margin', '*', 'totalNetProceeds]
 * Example Out 1: 'margin * SUM(totalNetProceeds)'
 *
 * Example in 2: ['unitCost' + 'soldCost']
 * Example out 2: 'SUM(unitCost) + SUM(soldCost)'
 *
 * Example in 3: ['unitCost' + 'soldCost'] with AVG aggregate function
 * Example out 3: 'AVG(unitCost) + AVG(soldCost)'
 *
 * See more examples in the test file
 * @param tokens each token is something from columnNameOptions or an operator
 * @param columnNameOptions available column ids for the formula
 * @param aggregateFunction the aggregate function to use (SUM, AVG, COUNT)
 */
export const tokensToExpression = (
  tokens: string[],
  columnNameOptions: string[],
  aggregateFunction = AggregateFunction.SUM
): string => {
  let expression = '';
  tokens.forEach((token) => {
    if (
      columnNameOptions.some(
        (option) => option.toLowerCase() === token.toLowerCase()
      )
    ) {
      expression += reportValueColumnNameToExpression(token, aggregateFunction);
    } else {
      expression += token;
    }
  });

  return expression;
};

/**
 * Wraps an expression with a window function
 *
 * @param expression The expression to wrap
 * @param windowFunction The window function to use
 * @returns The wrapped expression
 */
export const applyWindowFunction = (
  expression: string,
  windowFunction: string
): string => {
  if (windowFunction === WindowFunction.RATIO_TO_REPORT) {
    return `100 * RATIO_TO_REPORT(${expression}) OVER ()`;
  }
  return expression;
};

/**
 * Detects if an expression has a window function and extracts the inner expression
 *
 * @param expression The expression to check
 * @returns An object with the detected window function and the inner expression
 */
export const detectWindowFunction = (
  expression: string
): { windowFunction: WindowFunction | null; innerExpression: string } => {
  // Check for the format with 100 * prefix
  const ratioToReportRegex =
    /^100\s*\*\s*RATIO_TO_REPORT\((.+)\)\s+OVER\s+\(\)$/i;
  const ratioToReportMatch = expression.match(ratioToReportRegex);

  if (ratioToReportMatch) {
    return {
      windowFunction: WindowFunction.RATIO_TO_REPORT,
      innerExpression: ratioToReportMatch[1],
    };
  }

  return {
    windowFunction: null,
    innerExpression: expression,
  };
};

export const isWindowFunction = (expression: string): boolean => {
  return detectWindowFunction(expression).windowFunction !== null;
};

/**
 * Detects the aggregate function used in an expression
 *
 * @param expression The expression to check
 * @returns The detected aggregate function or SUM as default
 */
export const detectAggregateFunction = (
  expression: string
): { aggregateFunction: AggregateFunction; cleanExpression: string } => {
  const avgRegex = /AVG\(([^)]+)\)/i;
  if (avgRegex.test(expression)) {
    return {
      aggregateFunction: AggregateFunction.AVG,
      cleanExpression: expression
        .replace(/AVG\(([^)]+)\)/gi, '$1')
        .replace(/\s+/g, ' ')
        .trim(),
    };
  }

  const countRegex = /COUNT\(([^)]+)\)/i;
  if (countRegex.test(expression)) {
    return {
      aggregateFunction: AggregateFunction.COUNT,
      cleanExpression: expression
        .replace(/COUNT\(([^)]+)\)/gi, '$1')
        .replace(/\s+/g, ' ')
        .trim(),
    };
  }

  return {
    aggregateFunction: AggregateFunction.SUM,
    cleanExpression: expression
      .replace(/SUM\(([^)]+)\)/gi, '$1')
      .replace(/\s+/g, ' ')
      .trim(),
  };
};

/**
 * Converts expression (representation in the backend) to token (representation on the UI)
 *
 * Example In 1: 'margin*SUM(totalNetProceeds)'
 * Example Out 1: ['margin', '*', 'totalNetProceeds]
 *
 * Example in 2: '(SUM(unitCost)+SUM(soldCost))*3'
 * Example out 2: ['(', 'unitCost' + 'soldCost', ')', '*', '3']
 *
 * Example in 3: 'RATIO_TO_REPORT(SUM(totalNetProceeds)) OVER ()'
 * Example out 3: ['totalNetProceeds'] (with window function detected separately)
 *
 * Example in 4: 'AVG(unitCost) + COUNT(soldCost)'
 * Example out 4: ['unitCost', '+', 'soldCost'] (with aggregate function detected separately)
 *
 * See more examples in the test file
 * @param expression expression to convert
 * @param columnNameOptions available column ids for the formula
 * @returns An array of tokens representing the expression
 */
export const expressionToTokens = (
  expression: string,
  columnNameOptions: string[]
): string[] => {
  const { innerExpression } = detectWindowFunction(expression);

  const tokens: string[] = [];
  let currentToken = '';

  const simplifiedExpression = innerExpression
    .replace(/SUM\(([^)]+)\)/gi, '$1')
    .replace(/AVG\(([^)]+)\)/gi, '$1')
    .replace(/COUNT\(([^)]+)\)/gi, '$1')
    .replace(/\s+/g, ' ')
    .trim();

  // Sort the column name options by length in descending order
  // This ensures that longer column names are matched first
  const columnExpressionOptions = columnNameOptions
    .map((option) => ({
      expression: option.toLowerCase(),
      columnName: option,
    }))
    .toSorted((a, b) => b.expression.length - a.expression.length);

  // Add the current token to the tokens array
  const addToken = () => {
    if (currentToken.trim() !== '') {
      tokens.push(currentToken.trim());
      currentToken = '';
    }
  };

  let i = 0;
  while (i < simplifiedExpression.length) {
    let foundMatch = false;
    for (const columnExpressionOption of columnExpressionOptions) {
      if (
        columnExpressionOption.expression ===
        simplifiedExpression
          .slice(i, i + columnExpressionOption.expression.length)
          .toLowerCase()
      ) {
        addToken(); // Add any previous token before the match
        tokens.push(columnExpressionOption.columnName); // Add the matched column expression
        i += columnExpressionOption.expression.length; // Move the index forward
        foundMatch = true;
        break; // Break out of the loop to start from the next character
      }
    }

    if (!foundMatch) {
      const char = simplifiedExpression[i];
      if (char === '(' || char === ')') {
        addToken(); // Add any previous token before the parenthesis
        tokens.push(char);
      } else if (char === '+' || char === '-' || char === '*' || char === '/') {
        addToken(); // Add any previous token before the operator
        tokens.push(char);
      } else {
        currentToken += char;
      }
      i++;
    }
  }

  addToken(); // Add any remaining token after the loop
  return tokens;
};

const VALIDATE_RANDOM_TIMES = 5;

/**
 * Validate the syntax of the formula by evaluating it with random numbers.
 * @param tokens
 * @param columnNameOptions
 * @returns
 */
export const validateTokensSyntax = (
  tokens: string[],
  columnNameOptions: string[]
): boolean => {
  // repeat VALIDATE_RANDOM_TIMES to make sure the formula is valid
  // needs to repeated in case of zero division or other random errors that are not syntax errors
  for (let i = 0; i < VALIDATE_RANDOM_TIMES; i++) {
    const columnNameOptionsToNumber = columnNameOptions.map((option) => ({
      option: option,
      number: Math.floor(Math.random() * 1000),
    }));

    let expression = '';
    tokens.forEach((token) => {
      if (
        columnNameOptions.some(
          (option) => option.toLowerCase() === token.toLowerCase()
        )
      ) {
        // replace column name with random number
        expression += columnNameOptionsToNumber.find(
          (option) => option.option.toLowerCase() === token.toLowerCase()
        )!.number;
      } else {
        expression += token;
      }
    });
    const result = evalulateConstantExpression(expression);
    if (result !== undefined) {
      return true;
    }
  }
  return false;
};

type CustomColumnFormulaEvaluator = {
  evaluate: () => number | null;
};

/**
 * Evaluate constant expression for basic validation of formula.
 *
 * Example expression: "(1 - 2) / 3 + 4 * 5"
 * @param formula
 * @param listingReportMetrics
 * @returns evaluated value, undefined on syntax error
 */
function evalulateConstantExpression(expression: string) {
  const code = `({
    evaluate: (): number | null => {
        var a = ${expression};
        if (isNaN(a) || !isFinite(a)) {
          throw new Error('NaN');
        }
        return a;
    }})`;

  const transpiledCode = ts.transpile(code);

  let result: number | null | undefined = undefined;
  try {
    const evaluator: CustomColumnFormulaEvaluator = eval(transpiledCode);
    result = evaluator.evaluate();
  } catch (error) {
    // Empty - result would be undefined
  }
  return result;
}
